import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Wallet, AlertCircle } from 'lucide-react';
import { ProgressStepper, PAYMENT_STEPS } from '../components/ProgressStepper';
import { usePaymentParams, useUpdatePaymentParams } from './PaymentRouter';
import { canEditWallet, isValidWalletAddress } from '../lib/urlParams';

export function WalletPage() {
  const navigate = useNavigate();
  const params = usePaymentParams();
  const updateParams = useUpdatePaymentParams();

  const [walletAddress, setWalletAddress] = useState(params?.walletAddress || '');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const walletEditable = !params || canEditWallet(params);
  const network = params?.network || '';
  const asset = params?.asset || 'USDT';

  // Validate on mount if wallet is pre-filled
  useEffect(() => {
    if (walletAddress && !walletEditable) {
      validateWallet();
    }
  }, []);

  function validateWallet(): boolean {
    setError(null);

    if (!walletAddress) {
      setError('Please enter a wallet address');
      return false;
    }

    if (!isValidWalletAddress(walletAddress, network)) {
      setError(`Invalid ${network} wallet address format`);
      return false;
    }

    return true;
  }

  async function handleContinue() {
    if (!validateWallet()) {
      return;
    }

    setLoading(true);

    try {
      // Update params with wallet address
      updateParams({ walletAddress });

      // Navigate to auth page
      navigate('/pay/auth');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleBack() {
    navigate('/pay');
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Stepper */}
      <ProgressStepper
        steps={PAYMENT_STEPS}
        currentStep="wallet"
        completedSteps={['amount']}
      />

      <div className="max-w-2xl mx-auto px-4 pb-8">
        {/* Custom branding */}
        {params?.logoUrl && (
          <div className="text-center mb-6">
            <img
              src={params.logoUrl}
              alt="Logo"
              className="h-12 w-auto mx-auto"
            />
          </div>
        )}

        <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
          Enter Your Wallet Address
        </h1>
        <p className="text-center text-gray-600 mb-6">
          This is where you'll receive your {asset}
        </p>

        {/* Main card */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {/* Transaction summary */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-600">You're buying</span>
              <span className="font-semibold text-gray-900">
                {params?.amount} {params?.currency === 'local' ? params.countryCode : asset}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Network</span>
              <span className="font-semibold text-gray-900">{network}</span>
            </div>
          </div>

          {/* Wallet address input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Wallet Address
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                <Wallet className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={walletAddress}
                onChange={(e) => {
                  setWalletAddress(e.target.value);
                  setError(null);
                }}
                placeholder={`Enter your ${network} address`}
                disabled={!walletEditable}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand outline-none font-mono text-sm disabled:bg-gray-100"
              />
            </div>
            {!walletEditable && (
              <p className="text-sm text-gray-500 mt-1">Wallet address is fixed for this payment link</p>
            )}
          </div>

          {/* Network info */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Important</p>
                <p>Make sure this address is correct. Funds sent to the wrong address cannot be recovered.</p>
                <p className="mt-2">This must be a <strong>{network}</strong> address for <strong>{asset}</strong>.</p>
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleBack}
              className="flex-1 py-3 px-6 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleContinue}
              disabled={loading || !walletAddress}
              className="flex-2 py-3 px-6 bg-brand text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-opacity"
              style={{ flex: 2 }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Validating...
                </>
              ) : (
                'Continue to Verification'
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Powered by{' '}
            <a
              href="https://pouchfinance.xyz"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand hover:text-brand-600 font-medium"
            >
              PouchPay
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
